import { CSS_SELECTOR, useCustomCss } from '../../contexts/CustomCss';
import { FunctionalComponent, h } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { Tab as StyledTab } from './Tabs.styles';
import { TabProps } from './interface';

import { useTabs } from './Tabs';

export const Tab: FunctionalComponent<TabProps> = ({ ariaControls, ariaLabel, children, forPanel, testid, id }) => {
  const { panelRefs, panels, select, selected, registerRef } = useTabs();
  const active = selected === forPanel;
  const { rules } = useCustomCss(CSS_SELECTOR.TAB_BUTTON);
  const tabRef = useRef<HTMLButtonElement>(null);

  const onClick = () => {
    select(forPanel);
  };

  useEffect(() => {
    if (panelRefs && panelRefs.length > 0 && active && panels) {
      const index = panels.indexOf(forPanel);
      panelRefs[index].current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    if (tabRef !== null) {
      registerRef(tabRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyPress = useCallback(
    (eventKey: string) => {
      if (panels && panelRefs) {
        const index = panels.indexOf(forPanel);
        const isLast = index === panels.length - 1;
        const isFirst = index === 0;

        if (eventKey === 'Tab') {
          // If we press the Tab here we should jump right into the content
          // Skipping the other tab regardless
        }

        if (eventKey === 'ArrowLeft') {
          // If it is the last tab, and the left arrow is pressed:
          // We should go to the previous tab
          if (isLast) {
            select(panels[index - 1]);
          }

          // If it is the first tab, and the left arrow is pressed:
          // We should go to the last tab
          if (isFirst) {
            select(panels[panels.length - 1]);
          }
        }

        if (eventKey === 'ArrowRight') {
          // If it is the last tab, and the right arrow is pressed:
          // We should go to the first tab
          if (isLast) {
            select(panels[0]);
          }

          // If it is the first tab, and the left arrow is pressed:
          // We should go to the next tab
          if (isFirst) {
            select(panels[index + 1]);
          }
        }
      }
    },
    [forPanel, panelRefs, panels, select],
  );

  return (
    <StyledTab
      aria-controls={ariaControls}
      tabIndex={active ? 0 : -1}
      ref={tabRef}
      onKeyDown={(event) => handleKeyPress(event.key)}
      id={id}
      active={active}
      aria-selected={active}
      aria-label={ariaLabel}
      data-testid={testid}
      type="button"
      role="tab"
      onClick={onClick}
      style={{ ...rules }}
    >
      {children}
    </StyledTab>
  );
};
